import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import logolight from "../../assets/images/optimize_logo dark.png";
import logodark from "../../assets/images/logo-dark.png";
import { useNavigate } from "react-router-dom";
import { Row, Col, CardBody, Card, Container, Form, Input, FormFeedback, Label } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";

// ─── Inject animations ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("fp-anim")) {
  const s = document.createElement("style");
  s.id = "fp-anim";
  s.textContent = `
    @keyframes fp-fadeIn  { from { opacity:0 } to { opacity:1 } }
    @keyframes fp-slideUp { from { opacity:0; transform:translateY(24px) } to { opacity:1; transform:translateY(0) } }
  `;
  document.head.appendChild(s);
}

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  primary: "#02a499",
  primaryDark: "#028f83",
  danger: "#e53e3e",
  text: "#1a202c",
  muted: "#718096",
  border: "#e2e8f0",
  bg: "#f8fafc",
  white: "#ffffff",
  success: "#38a169",
};

// ─── Shared popup styles ──────────────────────────────────────────────────────
const ps = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", animation: "fp-fadeIn 0.18s ease" },
  card: { background: C.white, borderRadius: 18, padding: "32px 28px 26px", width: "100%", maxWidth: 400, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", position: "relative", animation: "fp-slideUp 0.22s ease" },
  closeBtn: { position: "absolute", top: 14, right: 16, background: "none", border: "none", fontSize: 22, color: "#bbb", cursor: "pointer", lineHeight: 1 },
  iconWrap: (bg) => ({ width: 52, height: 52, borderRadius: "50%", background: bg || `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }),
  title: { fontSize: 17, fontWeight: 700, color: C.text, textAlign: "center", marginBottom: 6 },
  subtitle: { fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 22, lineHeight: 1.6 },
  label: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5, display: "block" },
  input: { width: "100%", padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, outline: "none", marginBottom: 4, boxSizing: "border-box", background: C.bg },
  inputErr: { borderColor: C.danger },
  errText: { color: C.danger, fontSize: 12, marginBottom: 10 },
  btnPrimary: { width: "100%", padding: "11px 0", borderRadius: 8, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer", marginTop: 6 },
  btnLink: { background: "none", border: "none", color: C.primary, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0 },
  otpBox: { width: 44, height: 50, textAlign: "center", fontSize: 20, fontWeight: 700, borderRadius: 8, border: `1.5px solid ${C.border}`, outline: "none", background: C.bg, color: C.text, transition: "border-color 0.15s" },
  pwWrap: { position: "relative", marginBottom: 4 },
  eyeBtn: { position: "absolute", top: "50%", right: 12, transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: 16, padding: 0 },
};

// ─── Password Input Component ─────────────────────────────────────────────────
const PasswordInput = ({ value, onChange, placeholder, show, onToggle, error }) => (
  <>
    <div style={{ position: "relative", marginBottom: 4 }}>
      <input
        type={show ? "text" : "password"} placeholder={placeholder} value={value}
        onChange={onChange}
        style={{ ...ps.input, marginBottom: 0, paddingRight: 40, ...(error ? ps.inputErr : {}) }}
      />
      <button type="button"
        style={{ position: "absolute", top: "50%", right: 12, transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: 16, padding: 0 }}
        onClick={onToggle}>
        <i className={show ? "mdi mdi-eye" : "mdi mdi-eye-off"} />
      </button>
    </div>
    {error && <div style={ps.errText}>{error}</div>}
  </>
);

// ─── Forgot Password Popup ────────────────────────────────────────────────────
const ForgotPasswordPopup = ({ onClose }) => {
  const [step, setStep] = useState("email"); // email | otp_reset | success
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwErrors, setPwErrors] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);
  const navigate = useNavigate();

  // useEffect(() => {
  //   if (resendTimer > 0) {
  //     const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
  //     return () => clearTimeout(t);
  //   }
  // }, [resendTimer]);

  // ── Step 1: Send OTP ──
  const handleSendOtp = async () => {
    if (!email) { setEmailError("Please enter your email"); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setEmailError("Please enter a valid email"); return; }
    setEmailError("");
    setIsLoading(true);
    try {
      await axios.post("https://api.hanoch.in/users/send-password-otp/", { email });
      toast.success("Password reset OTP sent!");
      setStep("otp_reset");
      // setResendTimer(60);
      // setTimeout(() => otpRefs.current[0]?.focus(), 120);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to send OTP");
    } finally { setIsLoading(false); }
  };

  // const handleResend = async () => {
  //   if (resendTimer > 0) return;
  //   setIsLoading(true);
  //   try {
  //     await axios.post("https://api.hanoch.in/users/send-password-otp/", { email });
  //     toast.success("OTP resent!");
  //     setOtp(["", "", "", "", "", ""]);
  //     setResendTimer(60);
  //     setTimeout(() => otpRefs.current[0]?.focus(), 80);
  //   } catch (err) {
  //     const errMsg = err.response?.data?.error || "";
  //     const match = errMsg.match(/wait (\d+) second/);
  //     if (match) setResendTimer(parseInt(match[1]));
  //     else toast.error(errMsg || "Failed to resend OTP");
  //   } finally { setIsLoading(false); }
  // };

  // ── Step 2: Reset Password ──
  const handleResetPassword = async () => {
    const otpVal = otp.join("");
    const errs = {};
    if (otpVal.length < 6) errs.otp = "Please enter the complete 6-digit OTP";
    if (!newPassword) errs.newPassword = "Please enter a new password";
    else if (newPassword.length < 6) errs.newPassword = "Password must be at least 6 characters";
    if (!confirmPassword) errs.confirmPassword = "Please confirm your password";
    else if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match";
    if (Object.keys(errs).length) { setPwErrors(errs); setOtpError(errs.otp || ""); return; }
    setPwErrors({}); setOtpError("");
    setIsLoading(true);
    try {
      await axios.post("https://api.hanoch.in/users/reset-password/", {
        email,
        otp: otpVal,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("Password reset successful!");
      setStep("success");
    } catch (err) {
      const data = err.response?.data;
      if (data?.otp) setOtpError(data.otp);
      else if (data?.new_password) setPwErrors((p) => ({ ...p, newPassword: data.new_password }));
      else toast.error(data?.message || data?.error || "Reset failed. Please try again.");
    } finally { setIsLoading(false); }
  };

  // ── OTP helpers ──
  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const u = [...otp]; u[i] = val.slice(-1); setOtp(u); setOtpError("");
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKey = (i, e) => { if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus(); };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!p) return;
    const u = [...otp]; p.split("").forEach((c, i) => { u[i] = c; }); setOtp(u);
    const n = u.findIndex((v) => !v); otpRefs.current[n === -1 ? 5 : n]?.focus();
  };

  return (
    <div style={ps.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={ps.card}>
        <button style={ps.closeBtn} onClick={onClose}>×</button>

        {/* ── Step 1: Email ── */}
        {step === "email" && (
          <>
            <div style={ps.iconWrap()}><i className="mdi mdi-lock-reset" style={{ color: "#fff", fontSize: 24 }} /></div>
            <div style={ps.title}>Forgot Password?</div>
            <div style={ps.subtitle}>Enter your email and we'll send you a one-time code to reset your password.</div>
            <label style={ps.label}>Email Address</label>
            <input
              autoFocus type="email" placeholder="Enter your email" value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              style={{ ...ps.input, ...(emailError ? ps.inputErr : {}) }}
            />
            {emailError && <div style={ps.errText}>{emailError}</div>}
            <button
              style={{ ...ps.btnPrimary, opacity: isLoading ? 0.75 : 1 }}
              onClick={handleSendOtp} disabled={isLoading}>
              {isLoading ? <span className="spinner-border spinner-border-sm text-white" /> : "Send Reset OTP"}
            </button>
          </>
        )}

        {/* ── Step 2: OTP + New Password ── */}
        {step === "otp_reset" && (
          <>
            <div style={ps.iconWrap()}><i className="ri-shield-keyhole-line" style={{ color: "#fff", fontSize: 22 }} /></div>
            <div style={ps.title}>Reset Password</div>
            <div style={ps.subtitle}>Enter the OTP sent to <strong style={{ color: C.text }}>{email}</strong> and set your new password.</div>

            {/* OTP boxes */}
            <label style={ps.label}>Verification Code</label>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 6 }} onPaste={handleOtpPaste}>
              {otp.map((d, i) => (
                <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                  type="text" inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKey(i, e)}
                  style={{ ...ps.otpBox, ...(d ? { borderColor: C.primary } : {}) }} />
              ))}
            </div>
            {otpError && <div style={{ ...ps.errText, textAlign: "center" }}>{otpError}</div>}

            {/* Resend */}
            {/* <div style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 18 }}>
              {resendTimer > 0
                ? <>Resend in <strong>{resendTimer}s</strong></>
                : <>Didn't receive it?{" "}<button style={ps.btnLink} onClick={handleResend} disabled={isLoading}>Resend OTP</button></>
              }
            </div> */}

            {/* New password */}
            <label style={ps.label}>New Password</label>
            <PasswordInput
              value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setPwErrors((p) => ({ ...p, newPassword: "" })); }}
              placeholder="Enter new password" show={showNew} onToggle={() => setShowNew((v) => !v)}
              error={pwErrors.newPassword}
            />

            {/* Confirm password */}
            <label style={{ ...ps.label, marginTop: 8 }}>Confirm Password</label>
            <PasswordInput
              value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setPwErrors((p) => ({ ...p, confirmPassword: "" })); }}
              placeholder="Confirm new password" show={showConfirm} onToggle={() => setShowConfirm((v) => !v)}
              error={pwErrors.confirmPassword}
            />

            <button
              style={{ ...ps.btnPrimary, opacity: isLoading ? 0.75 : 1 }}
              onClick={handleResetPassword} disabled={isLoading}>
              {isLoading ? <span className="spinner-border spinner-border-sm text-white" /> : "Reset Password"}
            </button>

            <div style={{ textAlign: "center", marginTop: 12 }}>
              <button style={{ ...ps.btnLink, color: C.muted, fontWeight: 600, fontSize: 12 }}
                onClick={() => { setStep("email"); setOtp(["","","","","",""]); setOtpError(""); setPwErrors({}); }}>
                ← Change Email
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Success ── */}
        {step === "success" && (
          <>
            <div style={ps.iconWrap(`linear-gradient(135deg, ${C.success}, #2f855a)`)}>
              <i className="ri-check-line" style={{ color: "#fff", fontSize: 26 }} />
            </div>
            <div style={ps.title}>Password Reset!</div>
            <div style={ps.subtitle}>Your password has been successfully updated. You can now log in with your new password.</div>
            <button style={ps.btnPrimary} onClick={() => { onClose(); navigate("/Signin"); }}>
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Login Page ───────────────────────────────────────────────────────────────
const Login = (props) => {
  document.title = "Login";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          "https://api.hanoch.in/users/api/token/", values);

        //const {}=response.data;
        console.log("name", response)
        localStorage.setItem("access", response?.access);
        localStorage.setItem("refresh", response?.refresh);
        localStorage.setItem("role", response?.user?.role);
        localStorage.setItem("name", response?.user?.first_name);
        localStorage.setItem("id", response?.user?.id);
        localStorage.setItem("companyid", response?.user?.company);
        toast.success("Login successful");
        // Navigate based on company
  if (response?.user?.company === 4) {
    navigate("/vesseldashboard");
  } else {
    navigate("/");
  }
      } catch (error) {
        console.error("Login error", error);
        toast.error(error.response?.data?.message || "Login failed");
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => { document.body.className = ""; };
  });

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img src={logodark} alt="" height="72" className="auth-logo logo-dark mx-auto" />
                        <img src={logolight} alt="" height="105" className="auth-logo logo-light mx-auto" />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">Welcome Back !</h4>
                    <p className="mb-5 text-center">Sign in to continue to OptiMize.</p>

                    <Form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">Email</Label>
                            <Input
                              name="email" className="form-control" placeholder="Enter email" type="email"
                              onChange={validation.handleChange} onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={validation.touched.email && validation.errors.email ? true : false}
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid"><div>{validation.errors.email}</div></FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-4">
                            <Label className="form-label">Password</Label>
                            <div style={{ position: "relative" }}>
                              <Input
                                name="password" value={validation.values.password || ""}
                                type={showPassword ? "text" : "password"} placeholder="Enter Password"
                                onChange={validation.handleChange} onBlur={validation.handleBlur}
                                invalid={validation.touched.password && validation.errors.password ? true : false}
                              />
                              <span onClick={() => setShowPassword((prev) => !prev)}
                                style={{ position: "absolute", top: "50%", right: "10px", transform: "translateY(-50%)", cursor: "pointer", color: "#999" }}>
                                {showPassword ? <i className="mdi mdi-eye"></i> : <i className="mdi mdi-eye-off"></i>}
                              </span>
                            </div>
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid"><div>{validation.errors.password}</div></FormFeedback>
                            ) : null}
                          </div>

                          {/* Remember me + Forgot Password */}
                          <Row>
                            <Col>
                              <div className="form-check">
                                <input type="checkbox" className="form-check-input" id="customControlInline" />
                                <label className="form-label form-check-label" htmlFor="customControlInline">Remember me</label>
                              </div>
                            </Col>
                            <Col className="col-7">
                              <div className="text-md-end mt-3 mt-md-0">
                                <button
                                  type="button"
                                  onClick={() => setShowForgot(true)}
                                  style={{ background: "none", border: "none", padding: 0, color: "#6c757d", fontSize: 13, cursor: "pointer" }}>
                                  <i className="mdi mdi-lock me-1"></i>Forgot password?
                                </button>
                              </div>
                            </Col>
                          </Row>

                          <div className="d-grid mt-4">
                            <button className="btn btn-primary waves-effect waves-light" type="submit" disabled={isLoading}>
                              {isLoading ? (
                                <div className="text-center">
                                  <div className="spinner-border text-white" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              ) : "Log In"}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Forgot Password Popup */}
      {showForgot && <ForgotPasswordPopup onClose={() => setShowForgot(false)} />}
    </React.Fragment>
  );
};

export default Login;

Login.propTypes = {
  history: PropTypes.object,
};